import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// 접근권한 관리 (관리탭 > 관리자 전용)
//
//  메뉴·기능별로 어떤 등급이 접근할 수 있는지 설정한다.
//  값은 rt_permissions 테이블에 저장되며, 변경 즉시 반영된다(서버 재시작 불필요).
//
// [안전장치]
//  · admin 은 체크를 해제할 수 없다 (관리자가 자기 기능에서 잠기는 것 방지)
//  · 🔒 표시 항목은 변경 불가 (권한 탈취·데이터 유출 위험)
//
// [연관]
//  - 백엔드 GET/PUT /api/permissions
//  - middleware/requirePerm.js 가 이 값을 읽어 접근을 통제
// ─────────────────────────────────────────────

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function PermissionManager({ cardStyle, titleStyle, subTitleStyle }) {
  const [grades, setGrades] = useState([]);
  const [perms, setPerms]   = useState([]);
  const [counts, setCounts] = useState([]);
  const [msg, setMsg]       = useState('');
  const [busy, setBusy]     = useState('');

  const load = () => {
    axios.get('/api/permissions', auth())
      .then(r => {
        setGrades(r.data.grades || []);
        setPerms(r.data.perms || []);
        setCounts(r.data.user_counts || []);
      })
      .catch(e => setMsg('❌ 불러오기 실패: ' + (e.response?.data?.error || e.message)));
  };
  useEffect(load, []);

  // 체크박스 토글 → 즉시 저장
  const toggle = async (perm, grade) => {
    if (perm.is_locked) return;
    if (grade === 'admin') return;   // admin 은 해제 불가

    const has = perm.grades.includes(grade);
    const next = has
      ? perm.grades.filter(g => g !== grade)
      : [...perm.grades, grade];

    setBusy(perm.perm_key);
    setMsg('');
    try {
      const res = await axios.put(`/api/permissions/${perm.perm_key}`,
                                  { grades: next }, auth());
      // 서버가 admin 을 보정해 돌려주므로 그 값을 반영한다
      setPerms(ps => ps.map(p =>
        p.perm_key === perm.perm_key ? { ...p, grades: res.data.grades } : p));
      setMsg(`✅ ${perm.label} 저장됨`);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.error || e.message));
      load();   // 실패 시 서버 상태로 되돌린다
    } finally {
      setBusy('');
    }
  };

  // 카테고리별로 묶어서 보여준다
  const cats = [...new Set(perms.map(p => p.category))];

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>🔐 접근권한 관리</div>
      <div style={subTitleStyle}>
        메뉴·기능별로 접근 가능한 등급을 설정합니다 (변경 즉시 적용)
      </div>

      {/* 현재 등급별 회원 수 - 판단에 참고 */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12,
                    padding:'8px 12px', background:'#f1f5f9', borderRadius:6 }}>
        <span style={{ fontSize:12, color:'#64748b' }}>현재 회원:</span>
        {counts.map(c => (
          <span key={c.grade} style={{ fontSize:12, color:'#475569' }}>
            {c.grade} <b>{c.cnt}</b>명
          </span>
        ))}
      </div>

      {msg && (
        <div style={{ marginBottom:10, fontSize:13,
                      color: msg.startsWith('✅') ? '#166534' : '#b91c1c' }}>
          {msg}
        </div>
      )}

      {cats.map(cat => (
        <div key={cat} style={{ marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#334155',
                        marginBottom:6, paddingBottom:4,
                        borderBottom:'1px solid #e2e8f0' }}>
            {cat}
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                <th style={{ ...th, width:'40%' }}>기능</th>
                {grades.map(g => (
                  <th key={g.value} style={{ ...th, textAlign:'center' }}>
                    {g.label.replace(/\s*\(.*\)/, '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perms.filter(p => p.category === cat).map(p => (
                <tr key={p.perm_key} style={{ borderTop:'1px solid #f1f5f9',
                      background: p.is_locked ? '#fafafa' : 'transparent' }}>
                  <td style={td}>
                    <div style={{ fontWeight:600, color: p.is_locked ? '#94a3b8' : '#1e293b' }}>
                      {p.is_locked && '🔒 '}{p.label}
                    </div>
                    {p.note && (
                      <div style={{ fontSize:11,
                        color: p.note.startsWith('⚠') ? '#b45309' : '#94a3b8' }}>
                        {p.note}
                      </div>
                    )}
                  </td>
                  {grades.map(g => {
                    const on = p.grades.includes(g.value);
                    const fixed = p.is_locked || g.value === 'admin';
                    return (
                      <td key={g.value} style={{ ...td, textAlign:'center' }}>
                        <input type="checkbox" checked={on} disabled={fixed || busy === p.perm_key}
                               onChange={() => toggle(p, g.value)}
                               style={{ width:16, height:16,
                                        cursor: fixed ? 'not-allowed' : 'pointer' }} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <p style={{ margin:'4px 0 0', fontSize:11, color:'#94a3b8', lineHeight:1.6 }}>
        ※ <b>Admin</b> 은 해제할 수 없습니다. 관리자가 자기 기능에서 잠기는 사고를 막기 위함입니다.<br/>
        ※ 🔒 항목은 보안상 변경할 수 없습니다 (권한 탈취·소스 유출 위험).<br/>
        ※ 등급 변경 후 해당 회원은 <b>재로그인</b>해야 반영됩니다 (토큰에 등급이 들어있음).
      </p>
    </div>
  );
}

const th = { padding:'6px 8px', textAlign:'left', fontWeight:600,
             color:'#64748b', fontSize:12 };
const td = { padding:'8px' };